import { doc, runTransaction, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * ID Generator Service
 * 
 * Generuje sekvenčné ID pre:
 * - Firmy: F0000-0001 (globálne, naprieč celým projektom)
 * - Objednávky/Offers: F0000-0001 (globálne, naprieč celým projektom)
 * - Team members: T0001 (lokálne pre každú firmu, každá firma začína od T0001)
 */

const COUNTERS_COLLECTION = 'counters';
const COMPANY_COUNTER_DOC = 'companyCounter';
const ORDER_COUNTER_DOC = 'orderCounter';

/**
 * Formátuje číslo do formátu F0000-0001 alebo T0001
 */
function formatId(prefix: string, number: number, isTeamMember: boolean = false): string {
  if (isTeamMember) {
    // T0001, T0002, atď.
    return `${prefix}${number.toString().padStart(4, '0')}`;
  } else {
    // F0000-0001, F0000-0002, atď.
    const part1 = Math.floor(number / 10000).toString().padStart(4, '0');
    const part2 = (number % 10000).toString().padStart(4, '0');
    return `${prefix}${part1}-${part2}`;
  }
}

/**
 * Parsuje ID a vráti číslo
 */
function parseId(id: string, isTeamMember: boolean = false): number {
  if (isTeamMember) {
    // T0001 -> 1
    const match = id.match(/^T(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  } else {
    // F0000-0001 -> 1
    const match = id.match(/^F(\d{4})-(\d{4})$/);
    if (match) {
      const part1 = parseInt(match[1], 10);
      const part2 = parseInt(match[2], 10);
      return part1 * 10000 + part2;
    }
    return 0;
  }
}

/**
 * Získa alebo vytvorí counter dokument
 */
async function getOrCreateCounter(counterDocId: string, initialValue: number = 0): Promise<number> {
  const counterRef = doc(db, COUNTERS_COLLECTION, counterDocId);

  return runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);

    if (!counterDoc.exists()) {
      // Vytvor nový counter
      transaction.set(counterRef, { value: initialValue });
      return initialValue;
    }

    const data = counterDoc.data();
    return data?.value ?? initialValue;
  });
}

/**
 * Zvýši counter a vráti novú hodnotu
 */
async function incrementCounter(counterDocId: string): Promise<number> {
  const counterRef = doc(db, COUNTERS_COLLECTION, counterDocId);

  return runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);

    let currentValue = 0;
    if (counterDoc.exists()) {
      const data = counterDoc.data();
      currentValue = data?.value ?? 0;
    }

    const newValue = currentValue + 1;
    transaction.set(counterRef, { value: newValue }, { merge: true });

    return newValue;
  });
}

/**
 * Získa najvyššie číslo team member ID pre danú firmu
 */
async function getMaxTeamMemberNumber(companyId: string): Promise<number> {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('companyId', '==', companyId)
    );

    const snapshot = await getDocs(usersQuery);
    if (snapshot.empty) {
      return 0;
    }

    let maxNumber = 0;
    snapshot.forEach((doc) => {
      const userData = doc.data();
      const teamMemberId = userData.teamMemberId;

      if (teamMemberId && typeof teamMemberId === 'string') {
        const number = parseId(teamMemberId, true);
        if (number > maxNumber) {
          maxNumber = number;
        }
      }
    });

    return maxNumber;
  } catch (error) {
    console.error('Error getting max team member number:', error);
    return 0;
  }
}

/**
 * Generuje ID pre firmu (F0000-0001, F0000-0002, ...)
 */
export async function generateCompanyId(): Promise<string> {
  try {
    const newNumber = await incrementCounter(COMPANY_COUNTER_DOC);
    return formatId('F', newNumber);
  } catch (error: any) {
    console.warn('Counter increment failed, using random ID fallback:', error);
    // Fallback: Generate random ID in format Fxxxx-xxxx
    // Range: 10000000 to 99999999 to insure F1000+
    const randomNum = Math.floor(Math.random() * 90000000) + 10000000;
    return formatId('F', randomNum);
  }
}

/**
 * Generuje ID pre objednávku/offer (F0000-0001, F0000-0002, ...)
 */
export async function generateOrderId(): Promise<string> {
  const newNumber = await incrementCounter(ORDER_COUNTER_DOC);
  return formatId('F', newNumber);
}

/**
 * Generuje ID pre team membera (T0001, T0002, ...)
 * Každá firma začína od T0001
 */
export async function generateTeamMemberId(companyId: string): Promise<string> {
  try {
    const maxNumber = await getMaxTeamMemberNumber(companyId);
    const newNumber = maxNumber + 1;
    return formatId('T', newNumber, true);
  } catch (error) {
    console.error('Error generating team member ID:', error);
    // Fallback - ak nie je možné zistiť max číslo, začíname od 1
    return formatId('T', 1, true);
  }
}

/**
 * Inicializuje countery (volať pri prvom spustení alebo migrácii)
 */
export async function initializeCounters(): Promise<void> {
  try {
    // Inicializuj company counter
    await getOrCreateCounter(COMPANY_COUNTER_DOC, 0);

    // Inicializuj order counter
    await getOrCreateCounter(ORDER_COUNTER_DOC, 0);
  } catch (error) {
    console.error('Error initializing counters:', error);
    throw error;
  }
}

